import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Building2, Headset, Car, Users, Loader2, ArrowLeft, Presentation } from "lucide-react";
import { toast } from "sonner";
import { DEMO_ROLE_OPTIONS } from "@/lib/demoMode";
import { BrandWordmark } from "@/components/BrandWordmark";

const ROLE_ICONS = { admin: Building2, office_staff: Headset, instructor: Car, parent: Users };

export default function DemoLanding() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [loadingRole, setLoadingRole] = useState(null);

  const enterDemo = async (role) => {
    setLoadingRole(role);
    try {
      const res = await api.post("/demo/login", { role });
      setUser(res.data.user);
      navigate("/", { replace: true });
      toast.success(`Welcome — exploring as ${res.data.user.full_name}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start the Presentation Mode demo. Please try again.");
    } finally {
      setLoadingRole(null);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="demo-landing-page">
      <div
        className="relative bg-cover bg-center"
        style={{ backgroundImage: "linear-gradient(180deg, hsla(222,47%,11%,0.78), hsla(222,47%,11%,0.92)), url(https://images.pexels.com/photos/9518018/pexels-photo-9518018.jpeg)" }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center space-y-4">
          <div className="flex justify-center">
            <div className="h-12 w-12 rounded-2xl bg-[hsl(var(--presentation))] text-white flex items-center justify-center">
              <Presentation className="h-6 w-6" />
            </div>
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white">DriveDesk Hub — Showcase Edition</h1>
          <p className="text-sm sm:text-base text-white/85 max-w-2xl mx-auto">
            An AI office manager, smart scheduling, and real-time business insights — working together in one
            platform. Choose the perspective that best matches your role to explore it firsthand.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-8 sm:-mt-10 pb-12 space-y-8">
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-5" data-testid="demo-role-grid">
          {DEMO_ROLE_OPTIONS.map((opt) => {
            const Icon = ROLE_ICONS[opt.role];
            const isLoading = loadingRole === opt.role;
            return (
              <Card key={opt.role} className="flex flex-col" data-testid={`demo-role-card-${opt.role}`}>
                <CardHeader>
                  <div className="h-11 w-11 rounded-xl bg-[hsl(var(--presentation-soft))] text-[hsl(var(--presentation))] flex items-center justify-center mb-2">
                    <Icon className="h-5 w-5" />
                  </div>
                  <CardTitle className="text-base">{opt.label}</CardTitle>
                  <CardDescription>{opt.description}</CardDescription>
                </CardHeader>
                <CardContent className="flex-1" />
                <CardFooter>
                  <Button
                    className="w-full bg-[hsl(var(--presentation))] hover:opacity-95 text-white"
                    onClick={() => enterDemo(opt.role)}
                    disabled={loadingRole !== null}
                    data-testid={`demo-enter-button-${opt.role}`}
                  >
                    {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Explore as {opt.label.split(" / ")[0]}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        <div
          className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 text-sm text-muted-foreground text-center"
          data-testid="demo-showcase-note"
        >
          This Showcase Edition includes several premium features to demonstrate the full capabilities of
          DriveDesk Hub. Every implementation is customized to each business. Packages begin at{" "}
          <span className="font-semibold text-foreground">$4,997</span>.
        </div>

        <div className="text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            data-testid="demo-back-to-login-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
          </Link>
        </div>
        <BrandWordmark />
      </div>
    </div>
  );
}
